import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { RequestService } from "services/RequestService";

// Import the MultipleSelect component
import MultipleSelect from "../../schemas/multipleSelect"; // Adjust the import path as necessary

function UpdateForm({ open, onClose, onSuccess, powerStation }) {
  const [formData, setFormData] = useState({
    name: "",
    capacity: "",
    adminIds: [], // Add adminIds to formData
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (powerStation) {
      console.log(powerStation)
      setFormData({
        name: powerStation.name || "",
        capacity: powerStation.capacity || "",
        adminIds: powerStation.admins.map(admin => admin.userId) || [],
      });
    }
  }, [powerStation]);
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleSelectChange = (adminIds) => {
    setFormData({
      ...formData,
      adminIds,
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "اسم المحطة مطلوب";
    }
    if (!formData.capacity) {
      newErrors.capacity = "السعة مطلوبة";
    } else if (isNaN(formData.capacity) || Number(formData.capacity) <= 0) {
      newErrors.capacity = "يجب أن تكون السعة رقماً موجباً";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await RequestService.put(`PowerStation/${powerStation.id}`, {
        name: formData.name,
        capacity: Number(formData.capacity),
        adminIds: formData.adminIds, // Include adminIds in the request
      });

      if (response.data && !response.data.error) {
        onSuccess(response);
        handleClose();
      } else {
        setErrors({
          submit: response.data?.message || "حدث خطأ أثناء تحديث المحطة",
        });
      }
    } catch (error) {
      console.error("Error updating power station:", error);
      setErrors({
        submit: error.response?.data?.message || "حدث خطأ أثناء تحديث المحطة",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("هل أنت متأكد من حذف هذه المحطة؟")) {
      setIsSubmitting(true);
      try {
        const response = await RequestService.delete(`PowerStation/${powerStation.id}`);
        if (response.data && !response.data.error) {
          onSuccess(response);
          handleClose();
        } else {
          setErrors({
            submit: response.data?.message || "حدث خطأ أثناء حذف المحطة",
          });
        }
      } catch (error) {
        console.error("Error deleting power station:", error);
        setErrors({
          submit: error.response?.data?.message || "حدث خطأ أثناء حذف المحطة",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      capacity: "",
      adminIds: [], // Reset adminIds
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      dir="rtl"
    >
      <DialogTitle>
        <MDTypography variant="h6">تعديل المحطة</MDTypography>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <MDBox pt={2} pb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  name="name"
                  label="اسم المحطة"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  type="number"
                  name="capacity"
                  label="السعة"
                  value={formData.capacity}
                  onChange={handleChange}
                  error={!!errors.capacity}
                  helperText={errors.capacity}
                />
              </Grid>
              <Grid item xs={12}>
                <MultipleSelect
                  adminIds={formData.adminIds} // Pass adminIds
                  onChange={handleSelectChange}
                />
              </Grid>
              {errors.submit && (
                <Grid item xs={12}>
                  <MDTypography color="error" variant="caption">
                    {errors.submit}
                  </MDTypography>
                </Grid>
              )}
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ 
          padding: 2, 
          display: 'flex', 
          justifyContent: 'space-between',
          '& > button': {
            marginLeft: '8px',
            marginRight: '0px',
          }
        }}>
          <MDBox display="flex" gap={1}>
            <MDButton type="submit" color="info" disabled={isSubmitting}>
              {isSubmitting ? "جاري التحديث..." : "تحديث"}
            </MDButton>
          </MDBox>
          <MDButton onClick={handleClose} color="secondary">
            إلغاء
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

UpdateForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  powerStation: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    capacity: PropTypes.number.isRequired,
    adminIds: PropTypes.arrayOf(PropTypes.string), 
    admins: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.string.isRequired,
        userName: PropTypes.string,
        email: PropTypes.string,
      })
      ),
  }),
};

export default UpdateForm;